/**
 * Patient Flow PDF Generator
 *
 * Generates a PDF for the label "Patient Flow" tab (src/tags/LabelPatientFlow.js)
 * that mirrors the on-screen visualization: rolled-up label rows pinned at the
 * top, then per-provider rows, each with a horizontal bar sized by shared
 * patients and colored by its label.
 *
 * Reuses the existing Doctivity gradient-header template
 * (drawPageHeader / drawContinuationPage / loadDoctivityLogo).
 *
 * The caller (component) supplies a fully-resolved render model — bar widths,
 * colors, formatted percentages — so this module stays pure drawing and does
 * not need the app's color/scale utilities.
 */

import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import { registerRobotoFont } from './robotoFont';
import { drawPageHeader, drawContinuationPage } from './chartRenderer';
import { loadDoctivityLogo } from './logoManager';

// ─── Layout constants ─────────────────────────────────────────────────────────
const LOGO_MAX_W = 90;
const LOGO_MAX_H = 22;
const LOGO_ASPECT = 261 / 59;
const CONTENT_L = 20;
const CONTENT_R = 20;
const FOOTER_RESERVE = 40;
const ROW_H = 16;
const BAR_H = 9;

const HEAD_BG = [235, 241, 248]; // #EBF1F8 — on-screen header row
const HEAD_TXT = [100, 116, 139]; // #64748B
const COLLAPSED_BG = [238, 246, 244]; // #EEF6F4 — on-screen collapsed row tint
const ZEBRA_BG = [248, 250, 252]; // #F8FAFC — on-screen even provider row
const NAME_TXT = [33, 37, 41]; // #212529
const SUB_TXT = [75, 85, 99]; // #4B5563
const NEUTRAL_BAR = [203, 213, 225]; // #CBD5E1

// ─── Material Icons glyph rasterizer ──────────────────────────────────────────
// Rasterize a Material Icons glyph to a PNG via the webfont already loaded by
// the app, so PDF markers match the on-screen icons ('hub' = affiliated
// provider #5391D5; 'sell' = rolled-up label, tinted its label color). Cached
// per glyph+color. Mirrors loadHubIcon in locationPdfGenerator.
const iconCache = {};
const loadMaterialIcon = async (glyph, color, size = 32) => {
  const cacheKey = `${glyph}:${color}`;
  if (cacheKey in iconCache) return iconCache[cacheKey];
  let dataUrl = null;
  try {
    await document.fonts.ready;
    const scale = 2; // render at 2× for a crisp PDF image
    const canvas = document.createElement('canvas');
    canvas.width = size * scale;
    canvas.height = size * scale;
    const ctx = canvas.getContext('2d');
    ctx.font = `${size * scale}px "Material Icons"`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(glyph, (size * scale) / 2, (size * scale) / 2);
    dataUrl = canvas.toDataURL('image/png');
  } catch (e) {
    dataUrl = null;
  }
  iconCache[cacheKey] = dataUrl;
  return dataUrl;
};

/** #RRGGBB → [r,g,b]; falls back to neutral grey on bad input. */
const hexToRgb = (hex) => {
  if (typeof hex === 'string') {
    const m = hex.replace('#', '');
    if (m.length === 6) {
      const n = parseInt(m, 16);
      if (!Number.isNaN(n)) return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
  }
  return NEUTRAL_BAR;
};

/** Place the cached logo at the top-right corner of the current page. */
const placeLogoOnPage = (doc, logo, pageWidth) => {
  if (!logo) return;
  let w = LOGO_MAX_W;
  let h = w / LOGO_ASPECT;
  if (h > LOGO_MAX_H) { h = LOGO_MAX_H; w = h * LOGO_ASPECT; }
  const lx = pageWidth - 15 - LOGO_MAX_W;
  doc.addImage(
    `data:image/${logo.type.toLowerCase()};base64,${logo.data}`,
    logo.type,
    lx, 5, w, h,
  );
};

/** Ellipsize `text` to fit `maxW` points at the current font settings. */
const fit = (doc, text, maxW) => {
  let s = String(text ?? '');
  if (doc.getTextWidth(s) <= maxW) return s;
  while (s.length > 1 && doc.getTextWidth(`${s}…`) > maxW) s = s.slice(0, -1);
  return `${s}…`;
};

/**
 * Build the jsPDF document for the patient flow visualization.
 *
 * @param {Object}   opts
 * @param {string}   opts.title
 * @param {string}   [opts.subtitle]
 * @param {boolean}  [opts.showProviderCols] - legacy toggle for Specialty/Organization
 *   columns; used only when `columns` is not supplied.
 * @param {string[]} [opts.columns] - visible column ids, any of:
 *   'affiliated','label','provider','specialty','organization','shared_patients','pct'.
 *   When supplied it fully drives which columns render (from the export dialog's
 *   column picker). When omitted, all columns show (minus Specialty/Organization
 *   if showProviderCols is false).
 * @param {Array}    opts.rows - render model; each:
 *   { type: 'collapsed'|'provider', affiliated, label, labelColor, name,
 *     specialty, org, pctLabel, barPct (0-100), barColor }
 * @returns {Promise<jsPDF>}
 */
const ALL_COLS = [
  'affiliated',
  'label',
  'provider',
  'specialty',
  'organization',
  'shared_patients',
  'pct',
];
// Fixed pt widths for each (non-bar) column; the bar consumes the remainder.
const COL_W = {
  affiliated: 14,
  label: 88,
  provider: 130,
  specialty: 84,
  organization: 110,
  shared_patients: 14,
  pct: 42,
};

export const generatePatientFlowPDF = async ({
  title,
  subtitle = '',
  showProviderCols = true,
  columns = null,
  rows = [],
}) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  registerRobotoFont(doc);

  const pageWidth = doc.internal.pageSize.width; // 792
  const pageHeight = doc.internal.pageSize.height; // 612

  const logo = await loadDoctivityLogo();
  const placeLogo = () => placeLogoOnPage(doc, logo, pageWidth);

  // ── Column visibility ───────────────────────────────────────────────────────
  // `columns` (from the export dialog's picker) fully drives visibility when
  // present. Otherwise fall back to the legacy showProviderCols behavior.
  let visible;
  if (Array.isArray(columns)) {
    visible = new Set(columns);
  } else {
    visible = new Set(ALL_COLS);
    if (!showProviderCols) {
      visible.delete('specialty');
      visible.delete('organization');
    }
  }
  const has = (id) => visible.has(id);

  // ── Column geometry ────────────────────────────────────────────────────────
  const lx = CONTENT_L;
  const totalW = pageWidth - CONTENT_L - CONTENT_R;
  // X offset per visible column, in canonical order; hidden columns take no space.
  const xOf = {};
  let cx = lx;
  ALL_COLS.forEach((id) => {
    if (has(id)) {
      xOf[id] = cx;
      cx += COL_W[id];
    }
  });
  // The bar is the shared-patients visualization: it only renders when the
  // Shared Patients column is on, and fills the remaining width after the
  // last fixed column.
  const barVisible = has('shared_patients');
  const xBar = cx;
  const barW = barVisible ? Math.max(120, totalW - (cx - lx)) : 0;

  const drawColumnHeader = (y) => {
    doc.setFillColor(...HEAD_BG);
    doc.rect(lx, y, totalW, ROW_H, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...HEAD_TXT);
    const ty = y + 11;
    if (has('label')) doc.text('LABEL', xOf.label + 2, ty);
    if (has('provider')) doc.text('PROVIDER', xOf.provider + 2, ty);
    if (has('specialty')) doc.text('SPECIALTY', xOf.specialty + 2, ty);
    if (has('organization')) doc.text('ORGANIZATION', xOf.organization + 2, ty);
    if (has('shared_patients')) {
      doc.text('SHARED PATIENTS', xOf.shared_patients + 2, ty);
    } else if (has('pct')) {
      doc.text('% OF TOTAL', xOf.pct + 2, ty);
    }
    return y + ROW_H + 2;
  };

  // ── Page 1 header ──────────────────────────────────────────────────────────
  const { y: contentY } = drawPageHeader(doc, title || 'Patient Flow', subtitle, [], {
    pdfType: 'printable',
  });
  placeLogo();
  let y = drawColumnHeader(contentY);

  if (rows.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('No patient flow data for the current filters.', lx + 4, y + 16);
    return doc;
  }

  // Pre-render glyphs once so the row loop can draw them synchronously:
  // the affiliated 'hub' icon, and a 'sell' (tag) icon per distinct collapsed
  // label color (the icon is tinted its label's color on screen).
  const hubIcon = await loadMaterialIcon('hub', '#5391D5');
  const sellColors = [
    ...new Set(
      rows
        .filter((r) => r.type === 'collapsed')
        .map((r) => r.labelColor || '#00a887'),
    ),
  ];
  const sellIcons = Object.fromEntries(
    await Promise.all(
      sellColors.map(async (c) => [c, await loadMaterialIcon('sell', c)]),
    ),
  );
  // Grey patient-count glyphs shown just left of the bar: 'person' per provider,
  // 'group' per rolled-up label row (on-screen color #9CA3AF).
  const [personIcon, groupIcon] = await Promise.all([
    loadMaterialIcon('person', '#9CA3AF'),
    loadMaterialIcon('group', '#9CA3AF'),
  ]);

  let providerIndex = 0;
  rows.forEach((row) => {
    // Page break
    if (y + ROW_H > pageHeight - FOOTER_RESERVE) {
      doc.addPage();
      y = drawContinuationPage(doc, { pdfType: 'printable' });
      placeLogo();
      y = drawColumnHeader(y);
    }

    const isCollapsed = row.type === 'collapsed';

    // Row background (collapsed tint / provider zebra)
    if (isCollapsed) {
      doc.setFillColor(...COLLAPSED_BG);
      doc.rect(lx, y, totalW, ROW_H, 'F');
    } else {
      if (providerIndex % 2 === 1) {
        doc.setFillColor(...ZEBRA_BG);
        doc.rect(lx, y, totalW, ROW_H, 'F');
      }
      providerIndex += 1;
    }

    const midY = y + ROW_H / 2;
    const textY = y + 11;

    // Icon: the Material 'sell' (tag) glyph for rolled-up label rows, the 'hub'
    // glyph for affiliated providers — matching the on-screen markers. Falls
    // back to a colored swatch if the glyph couldn't be rasterized.
    if (has('affiliated')) {
      if (isCollapsed) {
        const sellIcon = sellIcons[row.labelColor || '#00a887'];
        if (sellIcon) {
          const s = 10;
          doc.addImage(sellIcon, 'PNG', xOf.affiliated + 2, midY - s / 2, s, s);
        } else {
          doc.setFillColor(...hexToRgb(row.barColor));
          doc.rect(xOf.affiliated + 2, midY - 4, 8, 8, 'F');
        }
      } else if (row.affiliated && hubIcon) {
        const s = 10;
        doc.addImage(hubIcon, 'PNG', xOf.affiliated + 2, midY - s / 2, s, s);
      }
    }

    // Label (colored)
    if (has('label')) {
      doc.setFont('helvetica', isCollapsed ? 'bold' : 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...(row.labelColor ? hexToRgb(row.labelColor) : SUB_TXT));
      doc.text(fit(doc, row.label, COL_W.label - 4), xOf.label + 2, textY);
    }

    // Provider name (or "N providers" for collapsed)
    if (has('provider')) {
      doc.setFont('helvetica', isCollapsed ? 'bold' : 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...(isCollapsed && row.labelColor ? hexToRgb(row.labelColor) : NAME_TXT));
      doc.text(fit(doc, row.name, COL_W.provider - 4), xOf.provider + 2, textY);
    }

    // Specialty / Organization
    if (has('specialty') || has('organization')) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...SUB_TXT);
      if (has('specialty') && row.specialty) {
        doc.text(fit(doc, row.specialty, COL_W.specialty - 4), xOf.specialty + 2, textY);
      }
      if (has('organization') && row.org) {
        doc.text(fit(doc, row.org, COL_W.organization - 4), xOf.organization + 2, textY);
      }
    }

    // Patient-count glyph just left of the bar ('group' for rolled-up rows,
    // 'person' for providers).
    if (has('shared_patients')) {
      const patientIcon = isCollapsed ? groupIcon : personIcon;
      if (patientIcon) {
        const s = 9;
        doc.addImage(patientIcon, 'PNG', xOf.shared_patients + 2, midY - s / 2, s, s);
      }
    }

    // Percent
    if (has('pct') && row.pctLabel) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(...SUB_TXT);
      doc.text(row.pctLabel, xOf.pct + 2, textY);
    }

    // Bar
    if (barVisible) {
      const pct = Math.max(0, Math.min(100, row.barPct || 0));
      if (pct > 0) {
        const w = Math.max(1.5, (pct / 100) * barW);
        doc.setFillColor(...hexToRgb(row.barColor));
        doc.roundedRect(xBar, midY - BAR_H / 2, w, BAR_H, 1.5, 1.5, 'F');
      }
    }

    y += ROW_H;
  });

  return doc;
};

/**
 * Generate the PDF and trigger a browser download.
 */
export const downloadPatientFlowPDF = async ({
  title,
  subtitle,
  showProviderCols,
  columns,
  rows,
  filenameBase = 'patient_flow',
  filename,
}) => {
  const doc = await generatePatientFlowPDF({
    title,
    subtitle,
    showProviderCols,
    columns,
    rows,
  });

  if (filename) {
    doc.save(filename);
    return;
  }
  const safeName = filenameBase.replace(/[^a-zA-Z0-9]/g, '_');
  const dateSuffix = format(new Date(), 'yy_MM_dd');
  doc.save(`doctivity_${safeName}_${dateSuffix}.pdf`);
};

/**
 * Generate the PDF and return it as a blob URL for the export-dialog preview.
 */
export const previewPatientFlowPDF = async (config) => {
  const doc = await generatePatientFlowPDF(config);
  const blob = doc.output('blob');
  return URL.createObjectURL(blob);
};

export default {
  generatePatientFlowPDF,
  downloadPatientFlowPDF,
  previewPatientFlowPDF,
};
